import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { QrCode, Scan, CheckCircle, XCircle, Clock, MapPin, Users, RefreshCw, Camera, X } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';

const EventScanner = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [qrCodeData, setQrCodeData] = useState('');
  const [scanning, setScanning] = useState(false);
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const html5QrCodeRef = useRef(null);
  const scannerRef = useRef(null);

  useEffect(() => {
    fetchActiveEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEventStats();
      fetchRecentCheckIns();
    }
  }, [selectedEvent]);

  const fetchActiveEvents = async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/events`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const today = new Date();
      const activeEvents = response.data.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= today || event.status === 'ongoing';
      });
      setEvents(activeEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        title: 'Error',
        description: 'Failed to load events',
        variant: 'destructive'
      });
    }
  };

  const fetchEventStats = async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await axios.get(
        `${backendUrl}/api/admin/events/${selectedEvent}/check-in-stats`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentCheckIns = async () => {
    if (!selectedEvent) return;
    
    try {
      const response = await axios.get(
        `${backendUrl}/api/admin/events/${selectedEvent}/recent-check-ins?limit=10`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setRecentCheckIns(response.data || []);
    } catch (error) {
      console.error('Error fetching check-ins:', error);
    }
  };

  const handleCheckIn = async () => {
    if (!qrCodeData.trim() || !selectedEvent) {
      toast({
        title: 'Error',
        description: 'Please select an event and scan a QR code',
        variant: 'destructive'
      });
      return;
    }

    try {
      setScanning(true);
      
      const response = await axios.post(
        `${backendUrl}/api/admin/events/${selectedEvent}/scan-check-in`,
        {
          qr_code_data: qrCodeData,
          admin_notes: ''
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      toast({
        title: 'Success!',
        description: `${response.data.user_name} checked in successfully`,
        variant: 'success'
      });

      // Refresh data
      setQrCodeData('');
      fetchEventStats();
      fetchRecentCheckIns();
      
    } catch (error) {
      console.error('Check-in error:', error);
      const errorMsg = error.response?.data?.detail || 'Check-in failed';
      toast({
        title: 'Check-in Failed',
        description: errorMsg,
        variant: 'destructive'
      });
    } finally {
      setScanning(false);
    }
  };

  const handleRefresh = () => {
    fetchEventStats();
    fetchRecentCheckIns();
  };

  const startCamera = async () => {
    try {
      setCameraError(null);
      setShowCamera(true);
      
      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (!scannerRef.current) {
        console.error('Scanner container not found');
        return;
      }

      // Initialize scanner
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await html5QrCodeRef.current.start(
        { facingMode: "environment" },
        config,
        (decodedText) => {
          // QR code detected
          setQrCodeData(decodedText);
          stopCamera();
          // Auto-submit after successful scan
          setTimeout(() => handleCheckIn(), 300);
        },
        (errorMessage) => {
          // Scanning in progress, ignore errors
        }
      );
      
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.message || 'Failed to access camera');
      setShowCamera(false);
    }
  };

  const stopCamera = async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping camera:', err);
    }
    setShowCamera(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(console.error);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* Mobile-First Header */}
      <div className="bg-[#2c3e7d] text-white py-6 px-4">
        <div className="container mx-auto max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <Scan size={32} />
            <div>
              <h1 className="text-2xl font-bold">Event Scanner</h1>
              <p className="text-sm text-gray-300">Admin Check-In Portal</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-2xl px-4 py-6 space-y-6">
        {/* Event Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Select Event</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose an event..." />
              </SelectTrigger>
              <SelectContent>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.title} - {new Date(event.event_date).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedEvent && (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Users className="mx-auto mb-2 text-blue-600" size={24} />
                    <p className="text-2xl font-bold">{stats.total_registered || 0}</p>
                    <p className="text-xs text-gray-600">Registered</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CheckCircle className="mx-auto mb-2 text-green-600" size={24} />
                    <p className="text-2xl font-bold">{stats.checked_in || 0}</p>
                    <p className="text-xs text-gray-600">Checked In</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Clock className="mx-auto mb-2 text-orange-600" size={24} />
                    <p className="text-2xl font-bold">{stats.pending || 0}</p>
                    <p className="text-xs text-gray-600">Pending</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* QR Scanner Input */}
            <Card className="border-2 border-[#2c3e7d]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Scan QR Code</CardTitle>
                  <QrCode className="text-[#2c3e7d]" size={24} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {!showCamera ? (
                  <>
                    <div className="flex gap-2">
                      <Button
                        onClick={startCamera}
                        className="flex-1 bg-[#2c3e7d] hover:bg-[#1e2a5a] py-6"
                        disabled={scanning}
                      >
                        <Camera className="mr-2" size={20} />
                        Open Camera Scanner
                      </Button>
                    </div>
                    
                    {cameraError && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                        Camera error: {cameraError}
                      </div>
                    )}

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">Or enter manually</span>
                      </div>
                    </div>
                    
                    <Input
                      type="text"
                      placeholder="Paste or enter QR code data..."
                      value={qrCodeData}
                      onChange={(e) => setQrCodeData(e.target.value)}
                      className="text-base py-5"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleCheckIn();
                        }
                      }}
                    />
                    
                    <Button 
                      onClick={handleCheckIn}
                      disabled={scanning || !qrCodeData.trim()}
                      className="w-full bg-green-600 hover:bg-green-700 text-lg py-6"
                    >
                      {scanning ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="mr-2" size={20} />
                          Check In Attendee
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bg-black rounded-lg overflow-hidden relative">
                      <div 
                        id="qr-reader" 
                        ref={scannerRef}
                        style={{ width: '100%' }}
                      ></div>
                      <Button
                        onClick={stopCamera}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700"
                        size="sm"
                      >
                        <X size={20} />
                      </Button>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">
                        Position QR code within the camera frame
                      </p>
                      <p className="text-xs text-gray-500">
                        Scanning will automatically check in the attendee
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Recent Check-Ins */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Recent Check-Ins</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleRefresh}
                  >
                    <RefreshCw size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {recentCheckIns.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">No check-ins yet</p>
                ) : (
                  <div className="space-y-3">
                    {recentCheckIns.map((checkIn, index) => {
                      const checkinTime = checkIn.checked_in_at || checkIn.checkin_timestamp || checkIn.created_at;
                      return (
                        <div 
                          key={index}
                          className="flex items-center justify-between py-3 border-b last:border-b-0"
                        >
                          <div>
                            <p className="font-medium">{checkIn.user_name}</p>
                            <p className="text-sm text-gray-600">
                              {checkinTime ? new Date(checkinTime).toLocaleTimeString() : 'Just now'}
                            </p>
                          </div>
                          <CheckCircle className="text-green-600" size={20} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default EventScanner;